/**
 * Projection between the connector's workflow snapshot and the `{states, globals}`
 * shape `POST /nlt/workflow-patch` expects — the workflow counterpart of
 * `prepareConnectorConfigForNltApi`.
 *
 * The snapshot keeps states and their edges apart (`graph.states` / `graph.adjacency`),
 * while ML wants each state to carry its own outgoing edges. Folding them in is the
 * forward projection; stripping them out again is the reverse one, and skipping that
 * strip would write `adjacency` into states where the DSL does not have it.
 */

import type { WorkflowEdge, WorkflowSnapshot, WorkflowState } from '../types/workflow';
import type { NltWorkflowProjection, NltWorkflowState } from '../types/nltWorkflow';
import type { EditableWorkflow } from './workflowDsl';
import { countStateAgents } from './workflowAgents';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * `product_config.config` holds the product-wide sections. It is an object on the
 * verified dev snapshot, but the connector stores some sibling configs as JSON
 * strings, so accept both rather than silently projecting empty globals.
 */
export function extractWorkflowGlobals(snapshot: WorkflowSnapshot | null): Record<string, unknown> {
  const raw = snapshot?.product_config?.config;
  if (isPlainObject(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (isPlainObject(parsed)) return parsed;
    } catch {
      // Fall through — an unparseable product config is reported as empty globals,
      // and `collectProjectionWarnings` tells the user what that costs.
    }
  }
  return {};
}

/** `null` when the snapshot carries no graph — there is nothing to tune yet. */
export function buildNltWorkflowProjection(snapshot: WorkflowSnapshot | null): NltWorkflowProjection | null {
  const graph = snapshot?.workflow?.graph;
  if (!graph || !isPlainObject(graph.states)) return null;

  const states: Record<string, NltWorkflowState> = {};
  Object.entries(graph.states).forEach(([id, state]) => {
    states[id] = { ...state, adjacency: graph.adjacency?.[id] ?? [] };
  });

  return { states, globals: extractWorkflowGlobals(snapshot) };
}

/**
 * Same projection, but from the graph currently open in the editor rather than the
 * stored snapshot — so the agent reasons about what the user is looking at, including
 * unsaved edits, instead of silently discarding them on the way back.
 */
export function buildProjectionFromEditable(
  wf: EditableWorkflow | null,
  snapshot: WorkflowSnapshot | null
): NltWorkflowProjection | null {
  if (!wf) return buildNltWorkflowProjection(snapshot);

  const outgoing: Record<string, WorkflowEdge[]> = {};
  wf.edges.forEach((edge) => {
    const { id, ...rest } = edge;
    (outgoing[edge.source] ||= []).push(rest as WorkflowEdge);
  });

  const states: Record<string, NltWorkflowState> = {};
  Object.entries(wf.states).forEach(([id, state]) => {
    states[id] = { ...state, adjacency: outgoing[id] ?? [] };
  });

  return { states, globals: extractWorkflowGlobals(snapshot) };
}

/**
 * Reverse projection: drop the `adjacency` we folded in, so the graph goes back to
 * the DSL shape where edges live in their own dictionary.
 */
export function stripProjectedAdjacency(
  states: Record<string, WorkflowState & { adjacency?: WorkflowEdge[] }>
): Record<string, WorkflowState> {
  const out: Record<string, WorkflowState> = {};
  Object.entries(states).forEach(([id, state]) => {
    const { adjacency, ...rest } = state;
    out[id] = rest as WorkflowState;
  });
  return out;
}

/**
 * The one global section that earns its weight when trimming.
 *
 * Global edits cannot be written from this connector at all — only `workflow` is
 * writable, everything else answers 403 — so the rest of `product_config` buys nothing
 * but proposals we have to refuse. `default_agents` is different: ML routes edits for
 * states with an empty roster into it, and without the section that turn comes back as
 * `unknown global section 'default_agents'`.
 */
const LOAD_BEARING_GLOBAL = 'default_agents';

/**
 * Drop the global sections that could never be written anyway, keeping `default_agents`
 * so roster routing still resolves. The result stays an object: the route rejects a
 * request whose `globals` is not one.
 */
export function trimGlobalsToWritableUse(globals: Record<string, unknown>): Record<string, unknown> {
  return LOAD_BEARING_GLOBAL in globals ? { [LOAD_BEARING_GLOBAL]: globals[LOAD_BEARING_GLOBAL] } : {};
}

/** Serialized byte size — used to show where the weight of a ~1 MB payload actually sits. */
export function approxJsonBytes(value: unknown): number {
  try {
    const json = JSON.stringify(value);
    if (!json) return 0;
    return typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(json).length : json.length;
  } catch {
    return 0;
  }
}

export function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Problems worth telling the user about *before* spending a real ML call.
 *
 * The roster trap is the expensive one: when a state has no agents of its own, the
 * live config lives in `globals.default_agents`, and ML routes the edit there. If that
 * section is missing the turn comes back as `unknown global section 'default_agents'`,
 * which reads like a bug in the request rather than a gap in the product config.
 */
export function collectProjectionWarnings(projection: NltWorkflowProjection): string[] {
  const warnings: string[] = [];

  const statesWithoutRoster = Object.values(projection.states)
    .filter((state) => countStateAgents(state.agents) === 0)
    .map((state) => state.state_id)
    .filter(Boolean);

  if (statesWithoutRoster.length > 0 && !('default_agents' in projection.globals)) {
    const shown = statesWithoutRoster.slice(0, 4).join(', ');
    const rest = statesWithoutRoster.length - Math.min(statesWithoutRoster.length, 4);
    warnings.push(
      `${statesWithoutRoster.length} state${statesWithoutRoster.length === 1 ? '' : 's'} (${shown}${rest > 0 ? `, +${rest}` : ''}) ` +
        'have no agent roster of their own, and this product config has no `default_agents` section. ' +
        'Asking to edit their agents will come back as “unknown global section”.'
    );
  }

  if (Object.keys(projection.globals).length === 0) {
    warnings.push(
      'The snapshot carries no `product_config` sections, so global edits (metaPrompts, configurator, …) cannot be addressed.'
    );
  }

  return warnings;
}

